import { useState, useEffect } from "react";
import { useAuth } from "../auth/AuthProvider";
import { PlotViewer } from "./PlotViewer";
import type { PlotData } from "@quanta/calcscript";

const API_BASE =
  import.meta.env.VITE_API_URL ||
  (window.location.hostname === "localhost"
    ? "http://localhost:3001"
    : `https://api.${window.location.hostname.replace(/^www\./, "")}`);

interface SharedHistoryEntry {
  id: string;
  input: string;
  output: string;
  type: string;
  result: unknown;
  isError: boolean;
  errorMessage: string | null;
  timestamp: number;
  authorName: string | null;
}

interface SharedSessionData {
  session: {
    guid: string;
    name: string;
    color: string;
    ownerName: string;
    shareMode: string;
  };
  permission: string;
  history: SharedHistoryEntry[];
}

export function SharedSessionViewer({ token }: { token: string }) {
  const { getAccessToken, isAuthenticated, isLoading: authLoading } = useAuth();
  const [data, setData] = useState<SharedSessionData | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);
  const [input, setInput] = useState("");
  const [submitting, setSubmitting] = useState(false);

  const fetchSession = async () => {
    try {
      const headers: Record<string, string> = {
        "Content-Type": "application/json",
      };
      if (isAuthenticated) {
        const accessToken = await getAccessToken();
        if (accessToken) {
          headers["Authorization"] = `Bearer ${accessToken}`;
        }
      }

      const res = await fetch(`${API_BASE}/api/shared/${token}`, { headers });
      if (!res.ok) {
        if (res.status === 403) {
          setError(
            "You don't have access to this session. Please sign in or request access.",
          );
        } else if (res.status === 404) {
          setError(
            "This shared session was not found or the link has expired.",
          );
        } else {
          setError("Failed to load shared session.");
        }
        return;
      }
      const json = await res.json();
      setData(json);
      setError(null);
    } catch {
      setError("Failed to connect to the server.");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (!authLoading) {
      fetchSession();
    }
  }, [token, authLoading, isAuthenticated]);

  // SSE for real-time updates
  useEffect(() => {
    if (!data) return;

    let eventSource: EventSource | null = null;
    try {
      eventSource = new EventSource(`${API_BASE}/api/shared/${token}/stream`);
      eventSource.onmessage = (event) => {
        try {
          const msg = JSON.parse(event.data);
          if (msg.type === "new-entry") {
            setData((prev) =>
              prev ? { ...prev, history: [...prev.history, msg.entry] } : prev,
            );
          }
        } catch {
          // Skip malformed messages
        }
      };
      eventSource.onerror = () => {
        // Will auto-reconnect
      };
    } catch {
      // SSE not supported or failed
    }

    return () => {
      eventSource?.close();
    };
  }, [data?.session.guid]);

  const handleSubmit = async () => {
    if (!input.trim() || !data) return;
    setSubmitting(true);
    try {
      const headers: Record<string, string> = {
        "Content-Type": "application/json",
      };
      if (isAuthenticated) {
        const accessToken = await getAccessToken();
        if (accessToken) {
          headers["Authorization"] = `Bearer ${accessToken}`;
        }
      }
      const res = await fetch(`${API_BASE}/api/shared/${token}/evaluate`, {
        method: "POST",
        headers,
        body: JSON.stringify({ input: input.trim() }),
      });
      if (res.ok) {
        const result = await res.json();
        // Add the entry locally (SSE will also deliver it but this is faster)
        setData((prev) =>
          prev
            ? {
                ...prev,
                history: [...prev.history, result.entry],
              }
            : prev,
        );
        setInput("");
      }
    } catch {
      // Silently fail
    } finally {
      setSubmitting(false);
    }
  };

  const canWrite =
    data?.permission === "manage" || data?.permission === "write";

  if (loading || authLoading) {
    return (
      <div className="shared-session-viewer">
        <div className="shared-session-loading">Loading shared session...</div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="shared-session-viewer">
        <div className="shared-session-error">
          <h2>Cannot Access Session</h2>
          <p>{error}</p>
          <a href="/" className="shared-session-home-link">
            Go to Quanta
          </a>
        </div>
      </div>
    );
  }

  if (!data) return null;

  const shareMode = data.session.shareMode;

  return (
    <div className="shared-session-viewer">
      <header className="shared-session-header">
        <div className="shared-session-title">
          <span
            className="shared-session-color"
            style={{ background: data.session.color }}
          />
          <h1>{data.session.name}</h1>
          <span
            className={`shared-session-badge ${shareMode === "public" ? "public" : "account"}`}
          >
            {shareMode === "public" ? "Public" : "Octagonl Only"}
          </span>
        </div>
        <div className="shared-session-meta">
          Shared by {data.session.ownerName || "Unknown"} &middot;{" "}
          {data.permission === "manage"
            ? "Manager"
            : data.permission === "write"
              ? "Editor"
              : data.permission === "readcopy"
                ? "Commenter"
                : "Viewer"}
        </div>
      </header>

      <div className="shared-session-history">
        {data.history.length === 0 ? (
          <div className="shared-session-empty">
            No calculations in this session yet.
          </div>
        ) : (
          data.history.map((entry, idx) => (
            <div
              key={entry.id || idx}
              className={`shared-entry ${entry.isError ? "error" : ""}`}
            >
              <div className="shared-entry-input">
                <span className="shared-entry-prompt">&gt;</span>
                <code>{entry.input}</code>
                {entry.authorName && (
                  <span className="shared-entry-author">
                    {entry.authorName}
                  </span>
                )}
              </div>
              {entry.isError ? (
                <div className="shared-entry-output error-text">
                  {entry.output}
                </div>
              ) : entry.type === "plot" && entry.result ? (
                <div className="shared-entry-output">
                  <PlotViewer data={entry.result as PlotData} />
                </div>
              ) : (
                <div className="shared-entry-output">
                  <span className="shared-entry-equals">=</span> {entry.output}
                </div>
              )}
            </div>
          ))
        )}
      </div>

      {canWrite && (
        <div className="shared-session-input-bar">
          <span className="shared-input-prompt">&gt;</span>
          <input
            type="text"
            className="shared-input"
            placeholder="Type an expression..."
            value={input}
            onChange={(e) => setInput(e.target.value)}
            onKeyDown={(e) => {
              if (e.key === "Enter" && !e.shiftKey) {
                e.preventDefault();
                handleSubmit();
              }
            }}
            disabled={submitting}
          />
          <button
            className="shared-input-run"
            onClick={handleSubmit}
            disabled={submitting || !input.trim()}
          >
            {submitting ? "..." : "Run"}
          </button>
        </div>
      )}
    </div>
  );
}
